import { createFileRoute } from '@tanstack/react-router'
import { track } from '@turfchale/analytics'
import { User, Users } from 'lucide-react'

import { Logo } from '@/components/ui/Logo'
import { useInstallPrompt } from '@/hooks/useInstallPrompt'

export const Route = createFileRoute('/install')({
  component: InstallPage,
})

function InstallCard({
  action,
  description,
  icon,
  title,
}: {
  action: React.ReactNode
  description: string
  icon: React.ReactNode
  title: string
}) {
  return (
    <div
      className="flex flex-col items-center text-center p-6 rounded-2xl gap-3"
      style={{ background: 'var(--bg2)', border: '1px solid var(--lime-line)' }}
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ background: 'var(--lime-dim)', color: 'var(--lime)' }}
      >
        {icon}
      </div>
      <h2 className="font-display font-bold text-lg" style={{ color: 'var(--text)' }}>
        {title}
      </h2>
      <p className="text-sm" style={{ color: 'var(--muted)' }}>
        {description}
      </p>
      <div className="w-full mt-2">{action}</div>
    </div>
  )
}

function InstallPage() {
  const { canInstall, install } = useInstallPrompt()

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--bg)' }}>
      <header
        className="flex items-center justify-center px-6 py-4"
        style={{ borderBottom: '1px solid var(--lime-line)' }}
      >
        <Logo external fontSize="1.4rem" />
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        <h1
          className="font-display font-bold text-center mb-2"
          style={{ color: 'var(--text)', fontSize: 'clamp(1.6rem,4vw,2.2rem)' }}
        >
          Install TurfChale
        </h1>
        <p className="text-center mb-10 max-w-md" style={{ color: 'var(--muted)' }}>
          Pick the app you need. Both install as a home-screen PWA — no app store required.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
          <InstallCard
            action={
              canInstall ? (
                <button
                  className="w-full text-sm font-bold px-5 py-2.5 rounded-full transition-opacity hover:opacity-85"
                  onClick={() => {
                    track('pwa_install_accepted')
                    install()
                  }}
                  style={{ background: 'var(--lime)', color: 'var(--bg)' }}
                >
                  Install Now
                </button>
              ) : (
                <p className="text-xs text-center" style={{ color: 'var(--muted)' }}>
                  Already installed, or open this page in Chrome/Safari to install.
                </p>
              )
            }
            description="Book turfs, manage bookings, and get slot reminders."
            icon={<User size={22} />}
            title="Install for Players"
          />

          <InstallCard
            action={
              <a
                className="w-full inline-flex items-center justify-center text-sm font-bold px-5 py-2.5 rounded-full transition-opacity hover:opacity-85"
                href="https://portal.turfchale.com/install"
                onClick={() => track('landing_cta_clicked', { cta: 'install' })}
                rel="noopener noreferrer"
                style={{ background: 'var(--lime)', color: 'var(--bg)' }}
                target="_blank"
              >
                Go to portal.turfchale.com
              </a>
            }
            description="Manage turfs, slots, bookings, and your team."
            icon={<Users size={22} />}
            title="Install for Owners"
          />
        </div>
      </main>
    </div>
  )
}
